import { createSlice } from '@reduxjs/toolkit';
import { LABELGENAPI } from '../../../API';

const emptySectionState = {
    data: [],
    loading: false,
    hasErrors: false,
};

export const shipFromSavingsSlice = createSlice({
    name: 'shipFromSavings',
    initialState: {
        storesData: {
            data: [],
            loading: false,
            hasErrors: false,
        },
        summary: {
            data: {},
            loading: false,
            hasErrors: false,
        },
        underused: { ...emptySectionState },
        routes: { ...emptySectionState },
        biggestWins: { ...emptySectionState },
        recentFlagged: { ...emptySectionState },
        groups: {
            data: [],
            meta: { total: 0, page: 1, page_size: 25, total_pages: 0 },
            loading: false,
            hasErrors: false,
        },
    },
    reducers: {
        getStores: (state) => {
            return { ...state, storesData: { ...state.storesData, loading: true } };
        },
        getStoresSuccess: (state, action) => {
            return {
                ...state,
                storesData: {
                    data: (action && action.payload && action.payload.data) || [],
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getStoresFailure: (state) => {
            return { ...state, storesData: { ...state.storesData, loading: false, hasErrors: true } };
        },

        getSummary: (state) => {
            return { ...state, summary: { ...state.summary, loading: true } };
        },
        getSummarySuccess: (state, action) => {
            return {
                ...state,
                summary: {
                    data: (action && action.payload && action.payload.data) || {},
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getSummaryFailure: (state) => {
            return { ...state, summary: { ...state.summary, loading: false, hasErrors: true } };
        },

        getUnderused: (state) => {
            return { ...state, underused: { ...state.underused, loading: true } };
        },
        getUnderusedSuccess: (state, action) => {
            return {
                ...state,
                underused: {
                    data: (action && action.payload && action.payload.data) || [],
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getUnderusedFailure: (state) => {
            return { ...state, underused: { ...state.underused, loading: false, hasErrors: true } };
        },

        getRoutes: (state) => {
            return { ...state, routes: { ...state.routes, loading: true } };
        },
        getRoutesSuccess: (state, action) => {
            return {
                ...state,
                routes: {
                    data: (action && action.payload && action.payload.data) || [],
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getRoutesFailure: (state) => {
            return { ...state, routes: { ...state.routes, loading: false, hasErrors: true } };
        },

        getBiggestWins: (state) => {
            return { ...state, biggestWins: { ...state.biggestWins, loading: true } };
        },
        getBiggestWinsSuccess: (state, action) => {
            return {
                ...state,
                biggestWins: {
                    data: (action && action.payload && action.payload.data) || [],
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getBiggestWinsFailure: (state) => {
            return { ...state, biggestWins: { ...state.biggestWins, loading: false, hasErrors: true } };
        },

        getRecentFlagged: (state) => {
            return { ...state, recentFlagged: { ...state.recentFlagged, loading: true } };
        },
        getRecentFlaggedSuccess: (state, action) => {
            return {
                ...state,
                recentFlagged: {
                    data: (action && action.payload && action.payload.data) || [],
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getRecentFlaggedFailure: (state) => {
            return { ...state, recentFlagged: { ...state.recentFlagged, loading: false, hasErrors: true } };
        },

        getGroups: (state) => {
            return { ...state, groups: { ...state.groups, loading: true } };
        },
        getGroupsSuccess: (state, action) => {
            const body = (action && action.payload && action.payload.data) || {};
            return {
                ...state,
                groups: {
                    data: body.rows || [],
                    meta: {
                        total: body.total || 0,
                        page: body.page || 1,
                        page_size: body.page_size || 25,
                        total_pages: body.total_pages || 0,
                    },
                    loading: false,
                    hasErrors: false,
                },
            };
        },
        getGroupsFailure: (state) => {
            return { ...state, groups: { ...state.groups, loading: false, hasErrors: true } };
        },
    },
});

export const {
    getStores, getStoresSuccess, getStoresFailure,
    getSummary, getSummarySuccess, getSummaryFailure,
    getUnderused, getUnderusedSuccess, getUnderusedFailure,
    getRoutes, getRoutesSuccess, getRoutesFailure,
    getBiggestWins, getBiggestWinsSuccess, getBiggestWinsFailure,
    getRecentFlagged, getRecentFlaggedSuccess, getRecentFlaggedFailure,
    getGroups, getGroupsSuccess, getGroupsFailure,
} = shipFromSavingsSlice.actions;

function buildQuery(params) {
    const qs = Object.keys(params)
        .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
    return qs ? `?${qs}` : '';
}

export const fetchDistinctStores = () => (dispatch) => {
    dispatch(getStores());
    try {
        LABELGENAPI
            .get('/reports/ship-from-savings/stores')
            .then((response) => dispatch(getStoresSuccess(response)))
            .catch((error) => dispatch(getStoresFailure(error.response)));
    } catch (error) {
        dispatch(getStoresFailure(error.response));
    }
};

export const fetchSummary = (storeId) => {
    const qs = buildQuery({ store_id: storeId });
    return (dispatch) => {
        dispatch(getSummary());
        LABELGENAPI
            .get(`/reports/ship-from-savings/summary${qs}`)
            .then((response) => dispatch(getSummarySuccess(response)))
            .catch((error) => dispatch(getSummaryFailure(error.response)));
    };
};

export const fetchUnderusedLocations = (storeId, limit = 5) => {
    const qs = buildQuery({ store_id: storeId, limit });
    return (dispatch) => {
        dispatch(getUnderused());
        LABELGENAPI
            .get(`/reports/ship-from-savings/underused-locations${qs}`)
            .then((response) => dispatch(getUnderusedSuccess(response)))
            .catch((error) => dispatch(getUnderusedFailure(error.response)));
    };
};

export const fetchRebalancingRoutes = (storeId, limit = 5) => {
    const qs = buildQuery({ store_id: storeId, limit });
    return (dispatch) => {
        dispatch(getRoutes());
        LABELGENAPI
            .get(`/reports/ship-from-savings/rebalancing-routes${qs}`)
            .then((response) => dispatch(getRoutesSuccess(response)))
            .catch((error) => dispatch(getRoutesFailure(error.response)));
    };
};

export const fetchBiggestWins = (storeId, limit = 3) => {
    const qs = buildQuery({ store_id: storeId, limit });
    return (dispatch) => {
        dispatch(getBiggestWins());
        LABELGENAPI
            .get(`/reports/ship-from-savings/biggest-wins${qs}`)
            .then((response) => dispatch(getBiggestWinsSuccess(response)))
            .catch((error) => dispatch(getBiggestWinsFailure(error.response)));
    };
};

export const fetchRecentFlagged = (storeId, limit = 5, search) => {
    const qs = buildQuery({ store_id: storeId, limit, search });
    return (dispatch) => {
        dispatch(getRecentFlagged());
        LABELGENAPI
            .get(`/reports/ship-from-savings/recent-flagged${qs}`)
            .then((response) => dispatch(getRecentFlaggedSuccess(response)))
            .catch((error) => dispatch(getRecentFlaggedFailure(error.response)));
    };
};

export const fetchGroups = (params = {}) => {
    const { storeId, outcome, page = 1, pageSize = 25 } = params;
    const qs = buildQuery({ store_id: storeId, outcome, page, page_size: pageSize });
    return (dispatch) => {
        dispatch(getGroups());
        LABELGENAPI
            .get(`/reports/ship-from-savings/groups${qs}`)
            .then((response) => dispatch(getGroupsSuccess(response)))
            .catch((error) => dispatch(getGroupsFailure(error.response)));
    };
};

// Convenience thunk to kick off all five fetches together, used in the
// page's useEffect on mount / filter change.
export const fetchAllShipFromSavings = (storeId) => {
    return (dispatch) => {
        dispatch(fetchSummary(storeId));
        dispatch(fetchUnderusedLocations(storeId));
        dispatch(fetchRebalancingRoutes(storeId));
        dispatch(fetchBiggestWins(storeId));
        // recentFlagged is fetched separately by the page (debounced,
        // with the order-id search term) - not included here to avoid
        // a duplicate request firing on every store change.
    };
};

export default shipFromSavingsSlice.reducer;